/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';
import { useApi } from 'shared_components/context';
import customFetch from 'shared_components/utils/customFetch';
import {
  CorrectRecordingRequestEditsInnerOpEnum,
  ChangeAssociatedArtistsRequestEditsInnerOpEnum,
  ManageContributionsRequestEditsInnerOpEnum,
  CreateArtistAndLinkRequestSourceEnum,
  CreateArtistAndLinkRequestFeaturedStatusEnum,
} from '../../../../shared_components/generated/admin';
import type {
  AdminClientList,
  ServiceResponse,
  LookupArtistsResponse,
  LookupClientAliasesResponse,
} from '../../../../shared_components/generated/admin';

// ── Shared Components ──

function StatusBadge({ method, path }: { method: string; path: string }) {
  const color =
    method === 'GET'
      ? 'tw-bg-blue-500/20 tw-text-blue-400 tw-border-blue-500/30'
      : 'tw-bg-green-500/20 tw-text-green-400 tw-border-green-500/30';
  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
      <span
        className={`tw-text-[11px] tw-font-bold tw-uppercase tw-px-2 tw-py-0.5 tw-rounded tw-border ${color}`}
      >
        {method}
      </span>
      <code className="tw-text-gray-400 tw-text-xs tw-font-mono">{path}</code>
    </div>
  );
}

function ResponseBlock({
  response,
  error,
  loading,
}: {
  response: unknown;
  error: string | null;
  loading: boolean;
}) {
  if (loading)
    return (
      <div className="tw-mt-3 tw-p-3 tw-rounded tw-bg-gray-800 tw-border tw-border-gray-700 tw-text-gray-400 tw-text-xs tw-font-mono tw-animate-pulse">
        Sending request...
      </div>
    );
  if (error)
    return (
      <div className="tw-mt-3 tw-p-3 tw-rounded tw-bg-red-900/30 tw-border tw-border-red-700/50 tw-text-red-300 tw-text-xs tw-font-mono tw-whitespace-pre-wrap tw-max-h-48 tw-overflow-y-auto">
        {error}
      </div>
    );
  if (response !== null)
    return (
      <div className="tw-mt-3 tw-p-3 tw-rounded tw-bg-gray-800 tw-border tw-border-gray-700 tw-text-green-300 tw-text-xs tw-font-mono tw-whitespace-pre-wrap tw-max-h-64 tw-overflow-y-auto">
        {JSON.stringify(response, null, 2)}
      </div>
    );
  return null;
}

function FieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="tw-flex tw-flex-col tw-gap-1">
      <label className="tw-text-gray-400 tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wider">
        {label}
      </label>
      {children}
    </div>
  );
}

function Input({
  value,
  onChange,
  placeholder,
  className = '',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-2.5 tw-py-1.5 tw-text-white tw-text-sm tw-font-mono tw-outline-none focus:tw-border-blue-500 tw-transition-colors ${className}`}
    />
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-2.5 tw-py-1.5 tw-text-white tw-text-sm tw-font-mono tw-outline-none focus:tw-border-blue-500 tw-transition-colors"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function ExecuteButton({
  onClick,
  loading,
}: {
  onClick: () => void;
  loading: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="tw-bg-blue-600 hover:tw-bg-blue-500 disabled:tw-bg-gray-600 disabled:tw-cursor-not-allowed tw-text-white tw-text-sm tw-font-medium tw-px-4 tw-py-1.5 tw-rounded tw-transition-colors tw-self-end"
    >
      {loading ? 'Executing...' : 'Execute'}
    </button>
  );
}

function EndpointCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="tw-border tw-border-gray-700 tw-rounded-lg tw-bg-gray-900/50 tw-overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-bg-gray-800/50 hover:tw-bg-gray-800 tw-transition-colors tw-border-b tw-border-gray-700"
      >
        <div className="tw-text-left">
          <h3 className="tw-text-white tw-font-semibold tw-text-sm">{title}</h3>
          <p className="tw-text-gray-400 tw-text-xs tw-mt-0.5">{description}</p>
        </div>
        <span className="tw-text-gray-500 tw-text-lg">
          {expanded ? '\u25B2' : '\u25BC'}
        </span>
      </button>
      {expanded && <div className="tw-p-5">{children}</div>}
    </div>
  );
}

// ── Global user selector ──

function GlobalUserSelector({
  users,
  usersLoading,
  usersError,
  selectedUserId,
  setSelectedUserId,
  recordingId,
  setRecordingId,
}: {
  users: AdminClientList[];
  usersLoading: boolean;
  usersError: string | null;
  selectedUserId: string;
  setSelectedUserId: (v: string) => void;
  recordingId: string;
  setRecordingId: (v: string) => void;
}) {
  return (
    <div className="tw-border tw-border-gray-700 tw-rounded-lg tw-bg-gray-900/80 tw-p-5 tw-mb-6">
      <h2 className="tw-text-white tw-font-semibold tw-text-sm tw-mb-3">
        Request Context
      </h2>
      <p className="tw-text-gray-500 tw-text-xs tw-mb-4">
        Select a user and recording ID to use across all endpoints below. Only
        onboarded users with UserSettings will work.
      </p>
      <div className="tw-grid tw-grid-cols-2 tw-gap-4">
        <FieldRow label="User">
          {usersLoading ? (
            <div className="tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-2.5 tw-py-1.5 tw-text-gray-500 tw-text-sm tw-animate-pulse">
              Loading users...
            </div>
          ) : usersError ? (
            <div className="tw-space-y-2">
              <div className="tw-text-red-400 tw-text-xs">{usersError}</div>
              <Input
                value={selectedUserId}
                onChange={setSelectedUserId}
                placeholder="Enter user ID manually"
              />
            </div>
          ) : (
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="tw-bg-gray-800 tw-border tw-border-gray-600 tw-rounded tw-px-2.5 tw-py-1.5 tw-text-white tw-text-sm tw-font-mono tw-outline-none focus:tw-border-blue-500 tw-transition-colors"
            >
              <option value="">-- Select a user --</option>
              {users.map((u) => (
                <option key={u.userId} value={u.userId ?? ''}>
                  {u.fullName || 'Unnamed'} (ID: {u.userId})
                  {u.nonClient ? ' [non-client]' : ''}
                </option>
              ))}
            </select>
          )}
        </FieldRow>
        <FieldRow label="Parent Recording ID">
          <Input
            value={recordingId}
            onChange={setRecordingId}
            placeholder="e.g. rec-abc-123"
          />
        </FieldRow>
      </div>
      {selectedUserId && (
        <div className="tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-text-xs">
          <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-green-500" />
          <span className="tw-text-gray-400">
            Using user{' '}
            <span className="tw-text-white tw-font-mono">{selectedUserId}</span>{' '}
            and recording{' '}
            <span className="tw-text-white tw-font-mono">
              {recordingId || '(empty)'}
            </span>
          </span>
        </div>
      )}
    </div>
  );
}

function ContextDisplay({
  userId,
  recordingId,
}: {
  userId: string;
  recordingId: string;
}) {
  return (
    <div className="tw-flex tw-items-center tw-gap-3 tw-text-[11px] tw-text-gray-500 tw-mb-3 tw-font-mono">
      <span>
        user_id= <span className="tw-text-gray-300">{userId || '?'}</span>
      </span>
      <span>
        parent_recording_id={' '}
        <span className="tw-text-gray-300">{recordingId || '?'}</span>
      </span>
    </div>
  );
}

// ── 1. Correct Recording Data (4.1.1) ──

function CorrectRecordingForm({
  userId,
  recordingId,
}: {
  userId: string;
  recordingId: string;
}) {
  const { adminApi: api } = useApi();
  const [edits, setEdits] = useState([
    { op: 'edit' as const, field: 'title', value: 'Updated Song Title' },
    { op: 'edit' as const, field: 'duration', value: '00:04:32' },
    { op: 'edit' as const, field: 'is_live', value: 'false' },
  ]);
  const [response, setResponse] = useState<ServiceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const updateEdit = (
    idx: number,
    key: 'op' | 'field' | 'value',
    val: string
  ) => {
    setEdits((prev) =>
      prev.map((e, i) => (i === idx ? { ...e, [key]: val } : e))
    );
  };

  const execute = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const result = await api.correctRecordingServiceResponse({
        userId,
        parentRecordingId: recordingId,
        correctRecordingRequest: {
          edits: edits.map((e) => ({
            op: e.op as CorrectRecordingRequestEditsInnerOpEnum,
            field: e.field,
            value: (e.value === 'true'
              ? true
              : e.value === 'false'
              ? false
              : e.value) as unknown as object,
          })),
        },
      });
      setResponse(result);
    } catch (err: any) {
      setError(err?.message || JSON.stringify(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <EndpointCard
      title="4.1.1 Correct Recording Data"
      description="Update/correct values on an existing recording (title, duration, ISRCs, live flag, version)"
    >
      <StatusBadge
        method="POST"
        path="/portal/users/{user_id}/primary-discography/recording/{parent_recording_id}/correct/"
      />
      <div className="tw-space-y-3">
        <ContextDisplay userId={userId} recordingId={recordingId} />
        <div className="tw-border tw-border-gray-700 tw-rounded tw-p-3 tw-space-y-2">
          <div className="tw-flex tw-items-center tw-justify-between">
            <span className="tw-text-gray-400 tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wider">
              Edits
            </span>
            <button
              type="button"
              onClick={() =>
                setEdits((prev) => [
                  ...prev,
                  { op: 'edit', field: '', value: '' },
                ])
              }
              className="tw-text-blue-400 tw-text-xs hover:tw-text-blue-300"
            >
              + Add edit
            </button>
          </div>
          {edits.map((edit, idx) => (
            <div
              key={idx}
              className="tw-grid tw-grid-cols-[100px_1fr_1fr_32px] tw-gap-2 tw-items-center"
            >
              <Select
                value={edit.op}
                onChange={(v) => updateEdit(idx, 'op', v)}
                options={[
                  { value: 'edit', label: 'edit' },
                  { value: 'add', label: 'add' },
                  { value: 'delete', label: 'delete' },
                ]}
              />
              <Input
                value={edit.field}
                onChange={(v) => updateEdit(idx, 'field', v)}
                placeholder="field name"
              />
              <Input
                value={edit.value}
                onChange={(v) => updateEdit(idx, 'value', v)}
                placeholder="value"
              />
              <button
                type="button"
                onClick={() =>
                  setEdits((prev) => prev.filter((_, i) => i !== idx))
                }
                className="tw-text-gray-500 hover:tw-text-red-400 tw-text-sm tw-transition-colors"
              >
                x
              </button>
            </div>
          ))}
        </div>
        <ExecuteButton onClick={execute} loading={loading} />
      </div>
      <ResponseBlock response={response} error={error} loading={loading} />
    </EndpointCard>
  );
}

// ── 2. Change Associated Artists (4.1.3) ──

function ChangeArtistsForm({
  userId,
  recordingId,
}: {
  userId: string;
  recordingId: string;
}) {
  const { adminApi: api } = useApi();
  const [edits, setEdits] = useState([
    { artistId: 'artist-001', op: 'add' as const, featuredStatus: 'ofa' },
    { artistId: 'artist-002', op: 'delete' as const, featuredStatus: 'nfa' },
  ]);
  const [response, setResponse] = useState<ServiceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const updateEdit = (
    idx: number,
    key: 'artistId' | 'op' | 'featuredStatus',
    val: string
  ) => {
    setEdits((prev) =>
      prev.map((e, i) => (i === idx ? { ...e, [key]: val } : e))
    );
  };

  const execute = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const result = await api.changeArtistsServiceResponse({
        userId,
        parentRecordingId: recordingId,
        changeAssociatedArtistsRequest: {
          edits: edits.map((e) => ({
            artistId: e.artistId,
            op: e.op as ChangeAssociatedArtistsRequestEditsInnerOpEnum,
            featuredStatus: e.featuredStatus,
          })),
        },
      });
      setResponse(result);
    } catch (err: any) {
      setError(err?.message || JSON.stringify(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <EndpointCard
      title="4.1.3 Change Associated Artists"
      description="Add/remove existing artists (alias/group) as main artists on a recording"
    >
      <StatusBadge
        method="POST"
        path="/portal/users/{user_id}/primary-discography/recording/{parent_recording_id}/artists/"
      />
      <div className="tw-space-y-3">
        <ContextDisplay userId={userId} recordingId={recordingId} />
        <div className="tw-border tw-border-gray-700 tw-rounded tw-p-3 tw-space-y-2">
          <div className="tw-flex tw-items-center tw-justify-between">
            <span className="tw-text-gray-400 tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wider">
              Edits
            </span>
            <button
              type="button"
              onClick={() =>
                setEdits((prev) => [
                  ...prev,
                  { artistId: '', op: 'add' as const, featuredStatus: 'ofa' },
                ])
              }
              className="tw-text-blue-400 tw-text-xs hover:tw-text-blue-300"
            >
              + Add edit
            </button>
          </div>
          {edits.map((edit, idx) => (
            <div
              key={idx}
              className="tw-grid tw-grid-cols-[1fr_100px_100px_32px] tw-gap-2 tw-items-center"
            >
              <Input
                value={edit.artistId}
                onChange={(v) => updateEdit(idx, 'artistId', v)}
                placeholder="artist_id"
              />
              <Select
                value={edit.op}
                onChange={(v) => updateEdit(idx, 'op', v)}
                options={[
                  { value: 'add', label: 'add' },
                  { value: 'delete', label: 'delete' },
                ]}
              />
              <Select
                value={edit.featuredStatus}
                onChange={(v) => updateEdit(idx, 'featuredStatus', v)}
                options={[
                  { value: 'fa', label: 'fa' },
                  { value: 'ofa', label: 'ofa' },
                  { value: 'nfa', label: 'nfa' },
                ]}
              />
              <button
                type="button"
                onClick={() =>
                  setEdits((prev) => prev.filter((_, i) => i !== idx))
                }
                className="tw-text-gray-500 hover:tw-text-red-400 tw-text-sm tw-transition-colors"
              >
                x
              </button>
            </div>
          ))}
        </div>
        <ExecuteButton onClick={execute} loading={loading} />
      </div>
      <ResponseBlock response={response} error={error} loading={loading} />
    </EndpointCard>
  );
}

// ── 3. Manage Contributions (4.2.1) ──

function ManageContributionsForm({
  userId,
  recordingId,
}: {
  userId: string;
  recordingId: string;
}) {
  const { adminApi: api } = useApi();
  const [edits, setEdits] = useState([
    {
      op: 'add' as const,
      role: 'writer',
      personaId: 'persona-001',
      dataCategory: '1',
      sourceUrl: 'https://example.com/evidence',
      notes: 'Confirmed via liner notes',
    },
    {
      op: 'edit' as const,
      role: 'producer',
      personaId: '',
      dataCategory: '3',
      sourceUrl: '',
      notes: 'Category updated',
    },
  ]);
  const [response, setResponse] = useState<ServiceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const updateEdit = (
    idx: number,
    key: keyof (typeof edits)[number],
    val: string
  ) => {
    setEdits((prev) =>
      prev.map((e, i) => (i === idx ? { ...e, [key]: val } : e))
    );
  };

  const execute = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const result = await api.manageContributionsServiceResponse({
        userId,
        parentRecordingId: recordingId,
        manageContributionsRequest: {
          edits: edits.map((e) => ({
            op: e.op as ManageContributionsRequestEditsInnerOpEnum,
            role: e.role,
            personaId: e.personaId || undefined,
            dataCategory: e.dataCategory || undefined,
            sourceUrl: e.sourceUrl || undefined,
            notes: e.notes || undefined,
          })),
        },
      });
      setResponse(result);
    } catch (err: any) {
      setError(err?.message || JSON.stringify(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <EndpointCard
      title="4.2.1 Manage Contributions"
      description="Add/delete/edit contributions (role/type) for an alias on a recording"
    >
      <StatusBadge
        method="POST"
        path="/portal/users/{user_id}/primary-discography/recording/{parent_recording_id}/contributions/"
      />
      <div className="tw-space-y-3">
        <ContextDisplay userId={userId} recordingId={recordingId} />
        <div className="tw-border tw-border-gray-700 tw-rounded tw-p-3 tw-space-y-3">
          <div className="tw-flex tw-items-center tw-justify-between">
            <span className="tw-text-gray-400 tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wider">
              Edits
            </span>
            <button
              type="button"
              onClick={() =>
                setEdits((prev) => [
                  ...prev,
                  {
                    op: 'add' as const,
                    role: '',
                    personaId: '',
                    dataCategory: '5',
                    sourceUrl: '',
                    notes: '',
                  },
                ])
              }
              className="tw-text-blue-400 tw-text-xs hover:tw-text-blue-300"
            >
              + Add edit
            </button>
          </div>
          {edits.map((edit, idx) => (
            <div
              key={idx}
              className="tw-border tw-border-gray-700/50 tw-rounded tw-p-2.5 tw-space-y-2"
            >
              <div className="tw-flex tw-items-center tw-justify-between">
                <span className="tw-text-gray-500 tw-text-[10px] tw-font-mono">
                  edit[{idx}]
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setEdits((prev) => prev.filter((_, i) => i !== idx))
                  }
                  className="tw-text-gray-500 hover:tw-text-red-400 tw-text-xs tw-transition-colors"
                >
                  remove
                </button>
              </div>
              <div className="tw-grid tw-grid-cols-3 tw-gap-2">
                <FieldRow label="op">
                  <Select
                    value={edit.op}
                    onChange={(v) => updateEdit(idx, 'op', v)}
                    options={[
                      { value: 'add', label: 'add' },
                      { value: 'delete', label: 'delete' },
                      { value: 'edit', label: 'edit' },
                    ]}
                  />
                </FieldRow>
                <FieldRow label="role">
                  <Input
                    value={edit.role}
                    onChange={(v) => updateEdit(idx, 'role', v)}
                    placeholder="e.g. writer"
                  />
                </FieldRow>
                <FieldRow label="persona_id">
                  <Input
                    value={edit.personaId}
                    onChange={(v) => updateEdit(idx, 'personaId', v)}
                    placeholder="optional"
                  />
                </FieldRow>
              </div>
              <div className="tw-grid tw-grid-cols-3 tw-gap-2">
                <FieldRow label="data_category">
                  <Select
                    value={edit.dataCategory}
                    onChange={(v) => updateEdit(idx, 'dataCategory', v)}
                    options={[
                      { value: '1', label: '1 - Verified' },
                      { value: '2', label: '2 - Inferred' },
                      { value: '3', label: '3 - Credited' },
                      { value: '4', label: '4 - Unverified' },
                      { value: '5', label: '5 - Unknown' },
                    ]}
                  />
                </FieldRow>
                <FieldRow label="source_url">
                  <Input
                    value={edit.sourceUrl}
                    onChange={(v) => updateEdit(idx, 'sourceUrl', v)}
                    placeholder="https://..."
                  />
                </FieldRow>
                <FieldRow label="notes">
                  <Input
                    value={edit.notes}
                    onChange={(v) => updateEdit(idx, 'notes', v)}
                    placeholder="optional"
                  />
                </FieldRow>
              </div>
            </div>
          ))}
        </div>
        <ExecuteButton onClick={execute} loading={loading} />
      </div>
      <ResponseBlock response={response} error={error} loading={loading} />
    </EndpointCard>
  );
}

// ── 4. Lookup Client Aliases (4.2.2) ──

function LookupClientAliasesForm({
  userId,
  recordingId,
}: {
  userId: string;
  recordingId: string;
}) {
  const { adminApi: api } = useApi();
  const [response, setResponse] = useState<LookupClientAliasesResponse | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const execute = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const result = await api.lookupClientAliasesLookupClientAliasesResponse({
        userId,
        parentRecordingId: recordingId,
      });
      setResponse(result);
    } catch (err: any) {
      setError(err?.message || JSON.stringify(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <EndpointCard
      title="4.2.2 Lookup Client Aliases"
      description="Retrieve client aliases (personas) for a westbury client"
    >
      <StatusBadge
        method="GET"
        path="/portal/users/{user_id}/primary-discography/recording/{parent_recording_id}/client-aliases/"
      />
      <div className="tw-space-y-3">
        <ContextDisplay userId={userId} recordingId={recordingId} />
        <ExecuteButton onClick={execute} loading={loading} />
      </div>
      <ResponseBlock response={response} error={error} loading={loading} />
    </EndpointCard>
  );
}

// ── 5. Lookup Artists (4.4.1) ──

function LookupArtistsForm({
  userId,
  recordingId,
}: {
  userId: string;
  recordingId: string;
}) {
  const { adminApi: api } = useApi();
  const [searchTerm, setSearchTerm] = useState('Beatles');
  const [response, setResponse] = useState<LookupArtistsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const execute = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const result = await api.lookupArtistsLookupArtistsResponse({
        userId,
        parentRecordingId: recordingId,
        searchTerm,
      });
      setResponse(result);
    } catch (err: any) {
      setError(err?.message || JSON.stringify(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <EndpointCard
      title="4.4.1 Lookup Artists"
      description="Retrieve artists from WMC database by search term (GraphDB)"
    >
      <StatusBadge
        method="GET"
        path="/portal/users/{user_id}/primary-discography/recording/{parent_recording_id}/artists/lookup/"
      />
      <div className="tw-space-y-3">
        <ContextDisplay userId={userId} recordingId={recordingId} />
        <FieldRow label="Search Term">
          <Input
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="e.g. Beatles"
          />
        </FieldRow>
        <ExecuteButton onClick={execute} loading={loading} />
      </div>
      <ResponseBlock response={response} error={error} loading={loading} />
    </EndpointCard>
  );
}

// ── 5b. Artist Lookup Live Search (integration test) ──

function ArtistLookupLiveSearchForm({
  userId,
  recordingId,
}: {
  userId: string;
  recordingId: string;
}) {
  const { adminApi: api } = useApi();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<{ id: string; stageName: string }[]>(
    []
  );
  const [selectedArtists, setSelectedArtists] = useState<
    { id: string; stageName: string; featuredStatus: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastPayload, setLastPayload] = useState<object | null>(null);

  // Debounced search
  useEffect(() => {
    if (!userId || !recordingId) return;
    setLoading(true);
    setError(null);
    const timer = setTimeout(async () => {
      try {
        const result = await api.lookupArtistsLookupArtistsResponse({
          userId,
          parentRecordingId: recordingId,
          searchTerm: searchQuery.trim() || '*',
        });
        setResults(
          (result.artists ?? []).map((a) => ({
            id: a.id ?? '',
            stageName: a.stageName ?? '',
          }))
        );
      } catch (err: any) {
        setError(err?.message || 'Lookup failed');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, userId, recordingId, api]);

  const addArtist = (artist: { id: string; stageName: string }) => {
    if (selectedArtists.some((a) => a.id === artist.id)) return;
    setSelectedArtists((prev) => [
      ...prev,
      { ...artist, featuredStatus: 'fa' },
    ]);
  };

  const removeArtist = (id: string) => {
    setSelectedArtists((prev) => prev.filter((a) => a.id !== id));
  };

  const toggleFeatured = (id: string) => {
    setSelectedArtists((prev) =>
      prev.map((a) =>
        a.id === id
          ? { ...a, featuredStatus: a.featuredStatus === 'fa' ? 'ofa' : 'fa' }
          : a
      )
    );
  };

  const buildPayload = () => {
    const payload = {
      edits: selectedArtists.map((a) => ({
        artist_id: a.id,
        op: 'add' as const,
        featuredStatus: a.featuredStatus,
      })),
    };
    setLastPayload(payload);
  };

  return (
    <EndpointCard
      title="Artist Lookup — Live Search Integration Test"
      description="Simulates the RecordingEditForm artist search: type to search, select artists, see the change_artists payload that would be sent"
    >
      <StatusBadge
        method="GET"
        path="/portal/users/{user_id}/primary-discography/recording/{parent_recording_id}/artists/lookup/"
      />
      <div className="tw-space-y-3">
        <ContextDisplay userId={userId} recordingId={recordingId} />

        {/* Search input */}
        <FieldRow label="Search Artists (debounced, 300ms)">
          <Input
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Type to search... (empty = all)"
          />
        </FieldRow>

        {/* Results */}
        <div className="tw-border tw-border-gray-700 tw-rounded tw-p-3">
          <span className="tw-text-gray-400 tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wider">
            Lookup Results{' '}
            {loading && (
              <span className="tw-text-blue-400 tw-normal-case">
                (searching...)
              </span>
            )}
          </span>
          {error && (
            <p className="tw-text-red-400 tw-text-xs tw-mt-1">{error}</p>
          )}
          <div className="tw-mt-2 tw-space-y-1 tw-max-h-48 tw-overflow-y-auto">
            {results.length === 0 && !loading && (
              <p className="tw-text-gray-500 tw-text-xs">No results</p>
            )}
            {results.map((artist) => {
              const alreadyAdded = selectedArtists.some(
                (a) => a.id === artist.id
              );
              return (
                <div
                  key={artist.id}
                  className="tw-flex tw-items-center tw-justify-between tw-px-2 tw-py-1.5 tw-rounded tw-bg-gray-800/50"
                >
                  <div>
                    <span className="tw-text-white tw-text-sm">
                      {artist.stageName}
                    </span>
                    <span className="tw-text-gray-500 tw-text-xs tw-ml-2 tw-font-mono">
                      {artist.id}
                    </span>
                  </div>
                  <button
                    type="button"
                    disabled={alreadyAdded}
                    onClick={() => addArtist(artist)}
                    className={`tw-text-xs tw-px-2 tw-py-0.5 tw-rounded tw-transition-colors ${
                      alreadyAdded
                        ? 'tw-bg-gray-700 tw-text-gray-500 tw-cursor-not-allowed'
                        : 'tw-bg-blue-600 tw-text-white hover:tw-bg-blue-500'
                    }`}
                  >
                    {alreadyAdded ? 'Added' : 'Add'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Selected artists */}
        {selectedArtists.length > 0 && (
          <div className="tw-border tw-border-gray-700 tw-rounded tw-p-3">
            <span className="tw-text-gray-400 tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wider">
              Selected Artists
            </span>
            <div className="tw-mt-2 tw-space-y-1">
              {selectedArtists.map((artist) => (
                <div
                  key={artist.id}
                  className="tw-flex tw-items-center tw-justify-between tw-px-2 tw-py-1.5 tw-rounded tw-bg-gray-800/50"
                >
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <span className="tw-text-white tw-text-sm">
                      {artist.stageName}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleFeatured(artist.id)}
                      className={`tw-text-[10px] tw-px-1.5 tw-py-0.5 tw-rounded tw-font-mono tw-transition-colors ${
                        artist.featuredStatus === 'fa'
                          ? 'tw-bg-green-600/20 tw-text-green-400 tw-border tw-border-green-600/30'
                          : 'tw-bg-gray-700 tw-text-gray-400 tw-border tw-border-gray-600'
                      }`}
                    >
                      {artist.featuredStatus === 'fa'
                        ? 'Featuring'
                        : 'Other Featured'}
                    </button>
                    <span className="tw-text-gray-600 tw-text-xs tw-font-mono">
                      {artist.id}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeArtist(artist.id)}
                    className="tw-text-gray-500 hover:tw-text-red-400 tw-text-xs tw-transition-colors"
                  >
                    remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Build payload */}
        <button
          type="button"
          onClick={buildPayload}
          disabled={selectedArtists.length === 0}
          className="tw-bg-blue-600 hover:tw-bg-blue-500 disabled:tw-bg-gray-600 disabled:tw-cursor-not-allowed tw-text-white tw-text-sm tw-font-medium tw-px-4 tw-py-1.5 tw-rounded tw-transition-colors"
        >
          Build change_artists Payload
        </button>
      </div>

      {lastPayload && (
        <div className="tw-mt-3">
          <span className="tw-text-gray-400 tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wider">
            Payload that would be sent to POST .../artists/
          </span>
          <div className="tw-mt-1 tw-p-3 tw-rounded tw-bg-gray-800 tw-border tw-border-gray-700 tw-text-green-300 tw-text-xs tw-font-mono tw-whitespace-pre-wrap tw-max-h-48 tw-overflow-y-auto">
            {JSON.stringify(lastPayload, null, 2)}
          </div>
        </div>
      )}
    </EndpointCard>
  );
}

// ── 6. Create Artist and Link (4.4.2) ──

function CreateArtistForm({
  userId,
  recordingId,
}: {
  userId: string;
  recordingId: string;
}) {
  const { adminApi: api } = useApi();
  const [stageName, setStageName] = useState('New Artist Name');
  const [source, setSource] = useState('discogs');
  const [sourceId, setSourceId] = useState('');
  const [featuredStatus, setFeaturedStatus] = useState('fa');
  const [isGroup, setIsGroup] = useState(false);
  const [members, setMembers] = useState<{ id: string; stageName: string }[]>(
    []
  );
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState<
    { id: string; stageName: string }[]
  >([]);
  const [memberSearchLoading, setMemberSearchLoading] = useState(false);
  const [response, setResponse] = useState<ServiceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Debounced member lookup
  useEffect(() => {
    if (!isGroup || !userId || !recordingId) return;
    const q = memberSearch.trim();
    if (!q) {
      setMemberResults([]);
      return;
    }
    setMemberSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await api.lookupArtistsLookupArtistsResponse({
          userId,
          parentRecordingId: recordingId,
          searchTerm: q,
        });
        setMemberResults(
          (result.artists ?? []).map((a) => ({
            id: a.id ?? '',
            stageName: a.stageName ?? '',
          }))
        );
      } catch {
        setMemberResults([]);
      } finally {
        setMemberSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearch, isGroup, userId, recordingId, api]);

  const execute = async () => {
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const result = await api.createArtistAndLinkServiceResponse({
        userId,
        parentRecordingId: recordingId,
        createArtistAndLinkRequest: {
          stageName,
          source: source as CreateArtistAndLinkRequestSourceEnum,
          sourceId: sourceId || undefined,
          featuredStatus:
            featuredStatus as CreateArtistAndLinkRequestFeaturedStatusEnum,
          members: isGroup && members.length > 0 ? members : undefined,
        },
      });
      setResponse(result);
    } catch (err: any) {
      setError(err?.message || JSON.stringify(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <EndpointCard
      title="4.4.2 Create Artist + Link"
      description="Create a new artist (alias/group) as main artist and link to recording. Includes members for groups."
    >
      <StatusBadge
        method="POST"
        path="/portal/users/{user_id}/primary-discography/recording/{parent_recording_id}/artists/create/"
      />
      <div className="tw-space-y-3">
        <ContextDisplay userId={userId} recordingId={recordingId} />
        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
          <FieldRow label="Stage Name">
            <Input
              value={stageName}
              onChange={setStageName}
              placeholder="Artist or group name"
            />
          </FieldRow>
          <FieldRow label="Source">
            <Select
              value={source}
              onChange={setSource}
              options={[
                { value: 'discogs', label: 'Discogs' },
                { value: 'MB', label: 'MusicBrainz' },
                { value: 'spotify', label: 'Spotify' },
                { value: 'deezer', label: 'Deezer' },
              ]}
            />
          </FieldRow>
        </div>
        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
          <FieldRow label="Source ID (optional)">
            <Input
              value={sourceId}
              onChange={setSourceId}
              placeholder="e.g. 12345"
            />
          </FieldRow>
          <FieldRow label="Featured Status">
            <Select
              value={featuredStatus}
              onChange={setFeaturedStatus}
              options={[
                { value: 'fa', label: 'fa (Featuring Artist)' },
                { value: 'ofa', label: 'ofa (Other Featured Artist)' },
              ]}
            />
          </FieldRow>
        </div>

        {/* Group toggle + members */}
        <div className="tw-flex tw-items-center tw-gap-2">
          <input
            type="checkbox"
            id="test-is-group"
            checked={isGroup}
            onChange={(e) => {
              setIsGroup(e.target.checked);
              if (!e.target.checked) {
                setMembers([]);
                setMemberSearch('');
              }
            }}
          />
          <label
            htmlFor="test-is-group"
            className="tw-text-white tw-text-sm tw-cursor-pointer"
          >
            This is a group / band
          </label>
        </div>

        {isGroup && (
          <div className="tw-border tw-border-gray-700 tw-rounded tw-p-3 tw-space-y-2">
            <span className="tw-text-gray-400 tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wider">
              Members (search to add from artist lookup)
            </span>
            {members.length > 0 && (
              <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                {members.map((m) => (
                  <div
                    key={m.id}
                    className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded tw-px-2 tw-py-1 tw-text-xs tw-bg-gray-800 tw-text-white tw-border tw-border-gray-600"
                  >
                    <span>{m.stageName}</span>
                    <span className="tw-text-gray-500 tw-font-mono">
                      {m.id}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setMembers((prev) => prev.filter((p) => p.id !== m.id))
                      }
                      className="tw-text-gray-500 hover:tw-text-red-400 tw-transition-colors"
                    >
                      x
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Input
              value={memberSearch}
              onChange={setMemberSearch}
              placeholder="Search for member..."
            />
            {memberSearch.trim() && (
              <div className="tw-max-h-36 tw-overflow-y-auto tw-space-y-1">
                {memberSearchLoading ? (
                  <p className="tw-text-gray-400 tw-text-xs">Searching...</p>
                ) : memberResults.filter(
                    (a) => !members.some((m) => m.id === a.id)
                  ).length === 0 ? (
                  <p className="tw-text-gray-500 tw-text-xs">No results</p>
                ) : (
                  memberResults
                    .filter((a) => !members.some((m) => m.id === a.id))
                    .map((artist) => (
                      <button
                        key={artist.id}
                        type="button"
                        onClick={() => {
                          setMembers((prev) => [...prev, artist]);
                          setMemberSearch('');
                        }}
                        className="tw-w-full tw-text-left tw-px-2 tw-py-1 tw-text-sm tw-text-white hover:tw-bg-gray-700 tw-rounded tw-transition-colors"
                      >
                        {artist.stageName}
                        <span className="tw-text-gray-500 tw-text-xs tw-ml-2 tw-font-mono">
                          {artist.id}
                        </span>
                      </button>
                    ))
                )}
              </div>
            )}
          </div>
        )}

        <ExecuteButton onClick={execute} loading={loading} />
      </div>
      <ResponseBlock response={response} error={error} loading={loading} />
    </EndpointCard>
  );
}

// ── Lookup Source Artist ──

function LookupSourceArtistForm({
  userId,
  recordingId,
}: {
  userId: string;
  recordingId: string;
}) {
  const [source, setSource] = useState('discogs');
  const [sourceId, setSourceId] = useState('');
  const [response, setResponse] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const execute = async () => {
    if (!sourceId.trim()) {
      setError('Source ID is required');
      return;
    }
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const url = `${
        process.env.WB_API_URL
      }/portal/users/${userId}/primary-discography/recording/${encodeURIComponent(
        recordingId
      )}/artists/lookup-source/?source=${source}&source_id=${encodeURIComponent(
        sourceId.trim()
      )}`;
      const resp = await customFetch(url);
      const data = await resp.json();
      if (!resp.ok) {
        setError(`${resp.status}: ${JSON.stringify(data)}`);
      } else {
        setResponse(data);
      }
    } catch (err: any) {
      setError(err?.message || JSON.stringify(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <EndpointCard
      title="Lookup Source Artist"
      description="Lookup artist from external source (Discogs/MusicBrainz) by source ID. Returns stage name and group members."
    >
      <StatusBadge
        method="GET"
        path="/portal/users/{user_id}/primary-discography/recording/{recording_id}/artists/lookup-source/"
      />
      <div className="tw-space-y-3">
        <ContextDisplay userId={userId} recordingId={recordingId} />
        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
          <FieldRow label="Source">
            <Select
              value={source}
              onChange={setSource}
              options={[
                { value: 'discogs', label: 'Discogs' },
                { value: 'musicbrainz', label: 'MusicBrainz' },
              ]}
            />
          </FieldRow>
          <FieldRow label="Source ID">
            <Input
              value={sourceId}
              onChange={setSourceId}
              placeholder="e.g. 31456"
            />
          </FieldRow>
        </div>
        <ExecuteButton onClick={execute} loading={loading} />
      </div>
      <ResponseBlock response={response} error={error} loading={loading} />
    </EndpointCard>
  );
}

// ── Page ──

// ── Dagster Run — Concurrency Guard (single-run guarantee) ──

interface ConcurrencyOutcome {
  index: number;
  outcome: 'started' | 'rejected' | 'error';
  httpStatus?: number;
  runId?: string | null;
  phase?: string | null;
  message?: string;
}

function DagsterRunConcurrencyForm({ userId }: { userId: string }) {
  const { adminApi: api } = useApi();
  const [count, setCount] = useState('5');
  const [running, setRunning] = useState(false);
  const [outcomes, setOutcomes] = useState<ConcurrencyOutcome[] | null>(null);
  const [finalStatus, setFinalStatus] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);
  // Editable so the test can target a different artist — e.g. when the selected
  // one has a stuck/corrupted run. Defaults to (and follows) the global selection.
  const [targetUserId, setTargetUserId] = useState(userId);
  useEffect(() => {
    setTargetUserId(userId);
  }, [userId]);

  const fireOnce = async (index: number): Promise<ConcurrencyOutcome> => {
    try {
      // 2xx (202) — this request claimed the run and a launch was started.
      const body = (await api.createPipelineRunStatus({
        pipelineRunRequest: { userId: targetUserId },
      })) as { runId?: string | null; phase?: string | null };
      return {
        index,
        outcome: 'started',
        httpStatus: 202,
        runId: body.runId ?? null,
        phase: body.phase ?? null,
      };
    } catch (e) {
      const httpStatus =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: Response }).response?.status
          : undefined;
      // 409 — the backend rejected this concurrent trigger (a run is already active).
      if (httpStatus === 409) return { index, outcome: 'rejected', httpStatus };
      return {
        index,
        outcome: 'error',
        httpStatus,
        message: (e as { message?: string })?.message ?? String(e),
      };
    }
  };

  const run = async () => {
    if (!targetUserId) return;
    const n = Math.max(2, Math.min(50, Number(count) || 0));
    setRunning(true);
    setError(null);
    setOutcomes(null);
    setFinalStatus(null);
    try {
      // Fire all requests at once so they race against the backend's row lock.
      const results = await Promise.all(
        Array.from({ length: n }, (_, i) => fireOnce(i + 1))
      );
      setOutcomes(results);
      // Fetch the resulting run so it's clear exactly one exists.
      try {
        setFinalStatus(
          await api.retrievePipelineRunStatus({ userId: targetUserId })
        );
      } catch {
        // The summary above already demonstrates the guarantee.
      }
    } catch (e) {
      setError((e as { message?: string })?.message ?? String(e));
    } finally {
      setRunning(false);
    }
  };

  const started = outcomes?.filter((o) => o.outcome === 'started').length ?? 0;
  const rejected =
    outcomes?.filter((o) => o.outcome === 'rejected').length ?? 0;
  const errored = outcomes?.filter((o) => o.outcome === 'error').length ?? 0;

  let verdict: { kind: 'pass' | 'warn' | 'fail'; text: string } | null = null;
  if (outcomes) {
    if (started > 1) {
      verdict = {
        kind: 'fail',
        text: `FAIL — ${started} runs started. The single-run guard did not hold.`,
      };
    } else if (started === 1) {
      verdict = {
        kind: 'pass',
        text: `PASS — exactly 1 run started; ${rejected} concurrent request(s) correctly rejected (409).`,
      };
    } else {
      verdict = {
        kind: 'warn',
        text: `No run started — the artist already had an active run, so all ${rejected} request(s) were rejected (409). No duplicate was created (guard held). Cancel/finish the current run and retry to see a fresh launch.`,
      };
    }
  }

  return (
    <EndpointCard
      title="Dagster Run — Concurrency Guard"
      description="Fire N concurrent run-trigger requests and verify only one Dagster run starts"
    >
      <StatusBadge
        method="POST"
        path="/portal/dagster/primary-discography/pipeline-runs/ ×N"
      />
      <ContextDisplay userId={targetUserId} recordingId="(n/a)" />
      <div className="tw-flex tw-items-end tw-gap-3 tw-flex-wrap">
        <FieldRow label="User ID to test">
          <Input
            value={targetUserId}
            onChange={setTargetUserId}
            placeholder="user id"
            className="tw-w-64"
          />
        </FieldRow>
        <FieldRow label="Concurrent requests">
          <Input
            value={count}
            onChange={setCount}
            placeholder="5"
            className="tw-w-24"
          />
        </FieldRow>
        <button
          type="button"
          disabled={running || !targetUserId}
          onClick={run}
          className="tw-bg-green-600 hover:tw-bg-green-500 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-text-white tw-text-sm tw-font-medium tw-px-4 tw-py-1.5 tw-rounded tw-transition-colors"
        >
          {running ? 'Firing…' : 'Fire concurrent requests'}
        </button>
      </div>

      {verdict && (
        <div
          className={[
            'tw-mt-4 tw-p-3 tw-rounded tw-border tw-text-sm',
            verdict.kind === 'pass'
              ? 'tw-bg-green-900/30 tw-border-green-700/50 tw-text-green-300'
              : verdict.kind === 'fail'
              ? 'tw-bg-red-900/30 tw-border-red-700/50 tw-text-red-300'
              : 'tw-bg-yellow-900/30 tw-border-yellow-700/50 tw-text-yellow-300',
          ].join(' ')}
        >
          {verdict.text}
        </div>
      )}

      {outcomes && (
        <div className="tw-mt-3 tw-grid tw-grid-cols-3 tw-gap-2 tw-text-center tw-text-xs">
          <div className="tw-p-2 tw-rounded tw-bg-gray-800 tw-border tw-border-gray-700">
            <div className="tw-text-green-400 tw-text-lg tw-font-bold">
              {started}
            </div>
            <div className="tw-text-gray-400">started (202)</div>
          </div>
          <div className="tw-p-2 tw-rounded tw-bg-gray-800 tw-border tw-border-gray-700">
            <div className="tw-text-yellow-400 tw-text-lg tw-font-bold">
              {rejected}
            </div>
            <div className="tw-text-gray-400">rejected (409)</div>
          </div>
          <div className="tw-p-2 tw-rounded tw-bg-gray-800 tw-border tw-border-gray-700">
            <div className="tw-text-red-400 tw-text-lg tw-font-bold">
              {errored}
            </div>
            <div className="tw-text-gray-400">errors</div>
          </div>
        </div>
      )}

      {outcomes && (
        <div className="tw-mt-3 tw-p-3 tw-rounded tw-bg-gray-800 tw-border tw-border-gray-700 tw-text-xs tw-font-mono tw-max-h-48 tw-overflow-y-auto tw-space-y-1">
          {outcomes.map((o) => (
            <div key={o.index} className="tw-flex tw-gap-2">
              <span className="tw-text-gray-500">#{o.index}</span>
              <span
                className={
                  o.outcome === 'started'
                    ? 'tw-text-green-300'
                    : o.outcome === 'rejected'
                    ? 'tw-text-yellow-300'
                    : 'tw-text-red-300'
                }
              >
                {o.outcome}
                {o.httpStatus ? ` (${o.httpStatus})` : ''}
              </span>
              {o.phase && (
                <span className="tw-text-gray-400">phase={o.phase}</span>
              )}
              {o.message && (
                <span className="tw-text-gray-400">{o.message}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {finalStatus !== null && (
        <div className="tw-mt-3">
          <div className="tw-text-gray-400 tw-text-[11px] tw-uppercase tw-tracking-wider tw-mb-1">
            Resulting run (GET status)
          </div>
          <ResponseBlock response={finalStatus} error={null} loading={false} />
        </div>
      )}

      {error && <ResponseBlock response={null} error={error} loading={false} />}
    </EndpointCard>
  );
}

export default function RecordingEndpointsTesting() {
  const { adminApi: api } = useApi();
  const [users, setUsers] = useState<AdminClientList[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [recordingId, setRecordingId] = useState('test-recording-001');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await api.listUsers();
        if (cancelled) return;
        setUsers(result);
        // Auto-select first user that has a userId
        const first = result.find((u) => u.userId);
        if (first?.userId) setSelectedUserId(first.userId);
      } catch (err: any) {
        if (!cancelled) setUsersError(err?.message || 'Failed to load users');
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  return (
    <div className="tw-min-h-screen tw-bg-gray-950 tw-text-white">
      <div className="tw-max-w-4xl tw-mx-auto tw-py-8 tw-px-6">
        <div className="tw-mb-6">
          <h1 className="tw-text-2xl tw-font-bold tw-text-white">
            Recording Management API Testing
          </h1>
          <p className="tw-text-gray-400 tw-text-sm tw-mt-1">
            BE-DS Service Contract endpoints (v0.1) — mock responses until DS
            implements
          </p>
          <div className="tw-flex tw-items-center tw-gap-4 tw-mt-3 tw-text-xs tw-text-gray-500">
            <span className="tw-flex tw-items-center tw-gap-1.5">
              <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-green-500" />
              Success response
            </span>
            <span className="tw-flex tw-items-center tw-gap-1.5">
              <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-red-500" />
              Error response
            </span>
            <span className="tw-flex tw-items-center tw-gap-1.5">
              <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-blue-500" />
              GET endpoints return data
            </span>
          </div>
        </div>

        <GlobalUserSelector
          users={users}
          usersLoading={usersLoading}
          usersError={usersError}
          selectedUserId={selectedUserId}
          setSelectedUserId={setSelectedUserId}
          recordingId={recordingId}
          setRecordingId={setRecordingId}
        />

        {!selectedUserId && !usersLoading && (
          <div className="tw-text-center tw-py-12 tw-text-gray-500 tw-text-sm">
            Select a user above to start testing endpoints.
          </div>
        )}

        {selectedUserId && (
          <div className="tw-space-y-4">
            <CorrectRecordingForm
              userId={selectedUserId}
              recordingId={recordingId}
            />
            <ChangeArtistsForm
              userId={selectedUserId}
              recordingId={recordingId}
            />
            <ManageContributionsForm
              userId={selectedUserId}
              recordingId={recordingId}
            />
            <LookupClientAliasesForm
              userId={selectedUserId}
              recordingId={recordingId}
            />
            <LookupArtistsForm
              userId={selectedUserId}
              recordingId={recordingId}
            />
            <ArtistLookupLiveSearchForm
              userId={selectedUserId}
              recordingId={recordingId}
            />
            <CreateArtistForm
              userId={selectedUserId}
              recordingId={recordingId}
            />
            <LookupSourceArtistForm
              userId={selectedUserId}
              recordingId={recordingId}
            />

            <div className="tw-pt-4 tw-mt-2 tw-border-t tw-border-gray-700">
              <h2 className="tw-text-white tw-font-semibold tw-text-sm">
                Dagster pipeline runs
              </h2>
              <p className="tw-text-gray-500 tw-text-xs tw-mt-0.5 tw-mb-3">
                Validate the single-run guarantee on the run-trigger endpoint.
              </p>
            </div>
            <DagsterRunConcurrencyForm userId={selectedUserId} />
          </div>
        )}

        <div className="tw-mt-8 tw-text-center tw-text-gray-600 tw-text-xs">
          Endpoints hit the local backend at {'{'}WB_API_URL{'}'}/portal/...
        </div>
      </div>
    </div>
  );
}
